import type { HTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

export interface ButtonGroupProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /**
   * Layout direction.
   * - 'row': buttons side by side
   * - 'column': buttons stacked
   * - 'responsive': stacked on mobile, row on desktop (default)
   */
  direction?: 'row' | 'column' | 'responsive';
  /**
   * Gap between buttons.
   * - 'sm': small gap
   * - 'md': medium gap (default)
   * - 'lg': large gap
   * - 'responsive': larger gap on mobile, smaller on desktop
   */
  gap?: 'sm' | 'md' | 'lg' | 'responsive';
  /**
   * Alignment of buttons.
   * - 'start': align to start
   * - 'center': center alignment
   * - 'end': align to end
   * - 'stretch': stretch to fill (useful for responsive mobile layouts)
   * - 'between': space between buttons
   */
  align?: 'start' | 'center' | 'end' | 'stretch' | 'between';
  /**
   * Whether buttons should take full width.
   * When true with direction='responsive', buttons are full width on mobile only.
   */
  fullWidth?: boolean;
  /**
   * Reverse the order of buttons.
   * Useful for RTL layouts or putting primary action last visually.
   */
  reverse?: boolean;
  /**
   * Wrap buttons to next line if they don't fit.
   */
  wrap?: boolean;
}

/**
 * A container component for grouping buttons with responsive layout support.
 *
 * @example Basic usage
 * ```tsx
 * <ButtonGroup>
 *   <Button variant="secondary">Cancel</Button>
 *   <Button variant="primary">Save</Button>
 * </ButtonGroup>
 * ```
 *
 * @example Responsive layout (stacked on mobile, row on desktop)
 * ```tsx
 * <ButtonGroup direction="responsive" gap="responsive" align="stretch">
 *   <Button responsive>Cancel</Button>
 *   <Button responsive>Save</Button>
 * </ButtonGroup>
 * ```
 *
 * @example Full width stacked buttons
 * ```tsx
 * <ButtonGroup direction="column" fullWidth>
 *   <Button>Option 1</Button>
 *   <Button>Option 2</Button>
 *   <Button>Option 3</Button>
 * </ButtonGroup>
 * ```
 */
export function ButtonGroup({
  children,
  direction = 'responsive',
  gap = 'md',
  align = 'start',
  fullWidth = false,
  reverse = false,
  wrap = false,
  className,
  ...props
}: ButtonGroupProps) {
  const directionClasses = {
    row: 'flex-row',
    column: 'flex-col',
    responsive: 'flex-col md:flex-row',
  };

  const gapClasses = {
    sm: 'gap-2',
    md: 'gap-3',
    lg: 'gap-4',
    responsive: 'gap-3 md:gap-2',
  };

  const alignClasses = {
    start: 'items-start justify-start',
    center: 'items-center justify-center',
    end: 'items-end justify-end',
    stretch: direction === 'responsive' ? 'items-stretch md:items-center' : 'items-stretch',
    between: 'items-center justify-between',
  };

  const classes = clsx(
    'flex',
    directionClasses[direction],
    gapClasses[gap],
    alignClasses[align],
    reverse &&
      (direction === 'column'
        ? 'flex-col-reverse'
        : direction === 'row'
          ? 'flex-row-reverse'
          : 'flex-col-reverse md:flex-row-reverse'),
    wrap && 'flex-wrap',
    fullWidth && 'w-full',
    className
  );

  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
}

ButtonGroup.displayName = 'ButtonGroup';
